import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material'
import { useEffect, useRef } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useMediaQuery } from 'react-responsive'
import { useNavigate } from 'react-router-dom'

import { useMessages } from '../../../../providers/Messages/Messages.provider'
import {
  AttachmentData,
  MessageData,
  MessageThreadData,
  MessageUser,
  UploadObj,
} from '../../../../providers/Messages/types'
import { ProviderUser } from '../../../../providers/ProviderDetails/types'
import { themeColors } from '../../../../theme'
import { getImageUrl } from '../../../../utilities/Storage'
import MessageEditor from './MessageEditor.view'
import { MessageTabs } from './Messaging.container'
import { useStyles } from './Messaging.styles'
import MobileMessage from './MobileMessage.view'

type MessageThreadProps = {
  activeTab: keyof MessageTabs
  messageThread: MessageThreadData
  backToMessages: () => void
  threadId: number
  attachmentKeys?: { id: number; key: string }[]
  setAttachmentKeys?: React.Dispatch<
    React.SetStateAction<{ id: number; key: string }[]>
  >
  uploads: UploadObj[]
  setUploads: React.Dispatch<React.SetStateAction<any>>
  isStandAloneView?: boolean
  myProviderDetails: ProviderUser | null
}

const MessageThread = ({
  activeTab,
  backToMessages,
  // messageThread,
  // threadId,
  attachmentKeys,
  setAttachmentKeys,
  uploads,
  setUploads,
  isStandAloneView,
  myProviderDetails,
}: MessageThreadProps) => {
  const go = useNavigate()

  const { classes } = useStyles()

  const { selectedThreadId, getProviderMessagesInThread, currentThread } =
    useMessages()

  useEffect(() => {
    if (selectedThreadId !== null) {
      getProviderMessagesInThread(selectedThreadId, 0)
    }
  }, [selectedThreadId])

  const processedKeysRef = useRef(new Set())

  const getAttachmentKeys = async () => {
    if (!currentThread?.messageThreadId) return

    const newAttachmentsToProcess = currentThread.messages.flatMap(
      (message: MessageData) =>
        message.attachments
          .filter(
            (a: AttachmentData) => !processedKeysRef.current.has(a.storageKey)
          )
          .map((a: AttachmentData) => ({
            messageId: message.messageId,
            attachment: a,
          }))
    )

    if (newAttachmentsToProcess.length === 0) return

    const newAttachments = await Promise.all(
      newAttachmentsToProcess.map(async ({ messageId, attachment }) => {
        const s3Key = (await getImageUrl(attachment.storageKey)) || ''
        processedKeysRef.current.add(attachment.storageKey)
        return {
          id: messageId,
          key: s3Key,
        }
      })
    )

    //@ts-ignore
    setAttachmentKeys((prev) => {
      const existingIds = new Set(prev.map((a) => a.id))
      const unique = newAttachments.filter((a) => !existingIds.has(a.id))
      if (unique.length === 0) return prev
      return [...prev, ...unique]
    })
  }

  useEffect(() => {
    getAttachmentKeys()
  }, [currentThread, selectedThreadId])

  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    // @ts-ignore
    messagesEndRef.current?.scrollIntoView({ animated: false })
  }
  useEffect(() => {
    scrollToBottom()
  }, [currentThread?.messages])

  const isMobile = useMediaQuery({ query: '(max-width: 480px)' })
  const mobileHeight = isStandAloneView
    ? 'calc(100vh - 380px)'
    : 'calc(100vh - 500px)'

  const handleViewChart = async () => {
    // await getPatientDetails(patientId)
    go(`/patientChart/${currentThread?.patientId}`)
  }

  const renderBackBtn = () => {
    const threadPatientNameObj = currentThread?.messages.find(
      (m) => m.fromUser.isPatient
    )
    let patientName = ''
    if (threadPatientNameObj) {
      patientName = threadPatientNameObj.fromUser.name
    } else {
      if (currentThread) {
        //in the case that we only have provider initial msg
        patientName = currentThread?.displayUser?.name ?? ''
      }
    }
    return (
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent='flex-start'
        sx={{
          position: 'relative',
          top: 0,
          marginTop: '0',
          zIndex: '100',
          backgroundColor: themeColors.white,
          paddingBottom: 2,
        }}
      >
        <button
          type='button'
          onClick={clearStates}
          className={classes.messageThread_backBtn}
        >
          &#8592;
        </button>
        <Typography sx={{ marginLeft: 2 }}>{patientName}</Typography>
        <Button
          onClick={handleViewChart}
          variant='contained'
          sx={{ marginLeft: 2 }}
        >
          View Chart
        </Button>
      </Box>
    )
  }

  const clearStates = () => {
    backToMessages()
  }

  const renderHoldStatusMessage = () => {
    console.log('threadPatientAccessRole: ', currentThread?.patientAccessRole)
    if (
      currentThread?.patientAccessRole?.description === 'Administrative Hold' ||
      currentThread?.patientAccessRole?.description === 'Financial Hold'
    ) {
      return (
        <Box
          style={{
            backgroundColor: themeColors.blue,
            padding: 20,
            borderRadius: 10,
          }}
        >
          <Typography color={themeColors.white} fontSize={18} fontWeight={700}>
            NOTICE: This patient currently is currently under{' '}
            {currentThread.patientAccessRole.description}.
          </Typography>
        </Box>
      )
    }
  }

  const renderReplyBox = () => {
    let threadPatientDeactivated = false
    let deactivatedProviderNameStr = ''
    // thread is with one patient/provider
    if (currentThread?.otherUsers.length === 1) {
      if (currentThread?.otherUsers[0].isPatient) {
        threadPatientDeactivated = currentThread.otherUsers[0].isActive
          ? false
          : true
      } else {
        deactivatedProviderNameStr = currentThread.otherUsers[0].isActive
          ? ''
          : currentThread.otherUsers[0].name
      }
    } else {
      const deactivatedArr = currentThread?.otherUsers.filter(
        (u: MessageUser) => !u.isActive
      )
      if (deactivatedArr) {
        threadPatientDeactivated = deactivatedArr.some(
          (u: MessageUser) => u.isPatient
        )
        if (!threadPatientDeactivated) {
          const deactivatedProviderArr = deactivatedArr.filter(
            (u: MessageUser) => !u.isPatient
          )
          const deactivatedProviderNameArr = deactivatedProviderArr.map(
            (u: MessageUser) => u.name
          )
          deactivatedProviderNameStr =
            deactivatedProviderNameArr.length > 1
              ? deactivatedProviderNameArr.join(', ')
              : deactivatedProviderNameArr.join('')
        }
      }
    }
    return (
      <Box
        display='flex'
        flexDirection='column'
        margin='15px 0'
        padding='0 10px'
        position='absolute'
        bottom={20}
        zIndex={100}
        style={{
          backgroundColor: themeColors.white,
          width: '95%',
        }}
      >
        {renderHoldStatusMessage()}
        {threadPatientDeactivated ? (
          <Box
            style={{
              backgroundColor: themeColors.blue,
              padding: 20,
              borderRadius: 10,
            }}
          >
            <Typography
              color={themeColors.white}
              fontSize={18}
              fontWeight={700}
            >
              NOTICE: This patient has been DEACTIVATED and cannot receive
              messages.
            </Typography>
          </Box>
        ) : (
          <>
            {deactivatedProviderNameStr ? (
              <Box
                style={{
                  backgroundColor: themeColors.blue,
                  padding: 20,
                  borderRadius: 10,
                }}
              >
                <Typography color={themeColors.red}>
                  NOTICE: {deactivatedProviderNameStr} is no longer a part of
                  the NorthStar team.
                </Typography>
              </Box>
            ) : null}
            <MessageEditor
              myProviderDetails={myProviderDetails}
              isStandAloneView={isStandAloneView}
              uploads={uploads}
              setUploads={setUploads}
              threadId={selectedThreadId !== null ? selectedThreadId : 0}
              thread={currentThread}
            />
          </>
        )}
      </Box>
    )
  }

  const fetchMoreMessages = () => {
    const oldestMessageId = currentThread?.messages.slice(-1)[0].messageId
    setTimeout(async () => {
      if (selectedThreadId !== null && oldestMessageId) {
        getProviderMessagesInThread(selectedThreadId, oldestMessageId)
      }
    }, 500)
  }

  const renderMessages = () => {
    if (currentThread) {
      return (
        <div
          id='scrollableDiv'
          style={{
            padding: '0 10px',
            marginTop: '30px',
            width: '100%',
          }}
        >
          <InfiniteScroll
            dataLength={currentThread.messages.length}
            next={fetchMoreMessages}
            hasMore={
              currentThread.messages.slice(-1)[0]?.isFirstMessageInThread
                ? false
                : true
            }
            inverse
            style={{
              display: 'flex',
              flexDirection: 'column-reverse',
              padding: '16px',
            }}
            height={isMobile ? mobileHeight : 'calc(100vh - 490px)'} //crucial for infinite scroll to work
            loader={
              <CircularProgress
                size={20}
                style={{
                  color: themeColors.gray40,
                  alignSelf: 'center',
                  margin: '30px 0',
                }}
              />
            }
            endMessage={
              <p style={{ textAlign: 'center', color: themeColors.gray60 }}>
                <Typography>You have seen all the messages.</Typography>
              </p>
            }
            scrollableTarget='scrollableDiv'
          >
            {currentThread.messages
              .sort(
                (a, b) =>
                  new Date(b.receivedDateTime).getTime() -
                  new Date(a.receivedDateTime).getTime()
              )
              .map((item, i) => (
                <Grid container key={`message-${i}`}>
                  <MobileMessage
                    myProviderDetails={myProviderDetails}
                    message={item}
                    keyValue={`mp${i}-${item.messageId}`}
                    attachmentKeys={attachmentKeys}
                  />
                </Grid>
              ))}
          </InfiniteScroll>
        </div>
      )
    }
  }

  const renderPage = () => {
    return (
      <div
        style={{
          padding: '8px 0',
          backgroundColor: themeColors.white,
          overflowY: 'scroll',
        }}
      >
        {renderBackBtn()}
        {renderMessages()}
        {renderReplyBox()}
        <div ref={messagesEndRef} />
      </div>
    )
  }

  return renderPage()
}

export default MessageThread
